import React, { useState, useEffect, useRef } from 'react';
import useSWR from 'swr';
import { fetchLoyaltyHistorySWR, getLoyaltyHistoryUrl } from './actions';
import './activity-panel.css';
import LottieAnimation from 'components/Lottie';

const ActivityPanel = ({
  isOpen,
  onClose,
  currentPoints,
  themeColors,
  userDetails,
  storeConfig,
  pointsTerminology = 'points',
  height,
}) => {
  // State to track loading more items
  const [loadingMore, setLoadingMore] = useState(false);
  // State to store all loaded history items
  const [historyItems, setHistoryItems] = useState([]);
  // State to track if there are more items to load
  const [hasMore, setHasMore] = useState(true);
  // State to store the timestamp for pagination
  const [lastTimeStamp, setLastTimeStamp] = useState('');
  // State to store the earnedPointsId for pagination (stored as a number)
  const [lastEarnedPointsId, setLastEarnedPointsId] = useState(null);

  // Reference to the container for scroll detection
  const containerRef = useRef(null);
  // Reference to the activity panel for click outside detection
  const panelRef = useRef(null);

  // Get necessary IDs for API calls
  const userId = userDetails?._id || '';
  const storeId = storeConfig?.storeId || '';
  const restaurantId = storeConfig?.restaurantId || '';

  // Initial fetch with SWR
  const {
    data: initialHistory,
    error,
    isLoading,
  } = useSWR(
    // Only fetch if we have the required IDs and the panel is open
    isOpen && userId && storeId && restaurantId
      ? getLoyaltyHistoryUrl(userId, storeId, restaurantId)
      : null,
    fetchLoyaltyHistorySWR,
    {
      revalidateOnFocus: false,
      dedupingInterval: 10000, // 10 seconds
    }
  );

  // Handle click outside to close panel
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if the click is on the activity button or its children (span.activity-text or caret icons)
      const isActivityButtonClick = event.target.closest('.activity-button');

      // Only close if click is outside the panel AND not on the activity button
      if (
        panelRef.current &&
        !panelRef.current.contains(event.target) &&
        !isActivityButtonClick &&
        isOpen
      ) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Update history items when initial data loads
  useEffect(() => {
    if (initialHistory && initialHistory.length > 0) {
      setHistoryItems(initialHistory);
      // Set the last timestamp for next page
      setLastTimeStamp(
        initialHistory[initialHistory.length - 1].timeStamp || ''
      );
      // Set the last earnedPointsId for next page as a number
      const earnedPointsId =
        initialHistory[initialHistory.length - 1].earnedPointsId;
      setLastEarnedPointsId(
        earnedPointsId !== undefined ? Number(earnedPointsId) : null
      );
      // Check if we have more data
      setHasMore(initialHistory.length >= 10);
    } else if (initialHistory && initialHistory.length === 0) {
      setHasMore(false);
    }
  }, [initialHistory]);

  // Function to load more history items
  const loadMoreHistory = async () => {
    if (!hasMore || loadingMore || !userId || !storeId || !restaurantId) return;

    setLoadingMore(true);

    try {
      // Get the URL with both timeStamp and earnedPointsId
      const url = getLoyaltyHistoryUrl(
        userId,
        storeId,
        restaurantId,
        0,
        0,
        10,
        lastTimeStamp,
        lastEarnedPointsId
      );

      const moreHistory = await fetchLoyaltyHistorySWR(url);

      if (moreHistory && moreHistory.length > 0) {
        setHistoryItems((prev) => [...prev, ...moreHistory]);

        // Update pagination parameters for next page
        const lastItem = moreHistory[moreHistory.length - 1];
        setLastTimeStamp(lastItem.timeStamp || '');

        // Set the earnedPointsId as a number
        const earnedPointsId = lastItem.earnedPointsId;
        setLastEarnedPointsId(
          earnedPointsId !== undefined ? Number(earnedPointsId) : null
        );

        setHasMore(moreHistory.length >= 10);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error('Error loading more history:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  // Handle scroll event for infinite scrolling
  const handleScroll = () => {
    const container = containerRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;

    // Load more when user scrolls to bottom (with a threshold)
    if (
      scrollHeight - scrollTop - clientHeight < 10 &&
      hasMore &&
      !loadingMore
    ) {
      loadMoreHistory();
    }
  };

  // Format date from timestamp - now returns day Month year format
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';

    const date = new Date(timestamp);
    return `${date.getDate()} ${date.toLocaleDateString('en-US', {
      month: 'long',
    })} ${date.getFullYear()}`;
  };

  // Get order number from description or use a placeholder
  const getOrderNumber = (item) => {
    if (!item || !item.description) return 'Order #0000';

    // Check if description contains order number pattern
    const orderMatch = item.description.match(/#(\d+)/);
    if (orderMatch && orderMatch[1]) {
      return `Order #${orderMatch[1]}`;
    }

    // If no order number found in description, use a generic order number or ID
    return `Order #${item.id?.slice(-4) || '0000'}`;
  };

  // If panel is not open, don't render
  if (!isOpen) return null;

  // Capitalize points terminology
  const capitalizedTerminology =
    pointsTerminology.charAt(0).toUpperCase() + pointsTerminology.slice(1);

  // Calculate panel height based on 5 items at 50px each, plus header space and gaps between items
  // Header height (h4 + margin) is approximately 50px
  // Each item is 50px and we need to add 7px gap between each (4 gaps for 5 items)
  const panelHeight = height || '350px'; // 250px for 5 items + 28px for 4 gaps + 50px for header

  return (
    <div
      className='activity-panel'
      style={{ height: historyItems.length === 0 ? '200px' : panelHeight }}
      ref={panelRef}
    >
      <div className='activity-history-section'>
        <h4>Balance Activity</h4>

        <div
          className='activity-list-container'
          ref={containerRef}
          onScroll={handleScroll}
        >
          {isLoading ? (
            <div className='activity-loading'>Loading history...</div>
          ) : error ? (
            <div className='activity-error'>Error loading history</div>
          ) : historyItems.length === 0 ? (
            <div className='activity-empty'>No activity history</div>
          ) : (
            <ul className='activity-list'>
              {historyItems.map((item, index) => (
                <li
                  key={`${item.timeStamp}-${index}`}
                  className='activity-item'
                >
                  <div className='activity-item-left'>
                    <div className='activity-item-date'>
                      {formatDate(item.timeStamp)}
                    </div>
                    <div className='activity-item-order'>
                      {getOrderNumber(item)}
                    </div>
                  </div>
                  <div className='activity-item-right'>
                    <div
                      className='activity-item-points-pill'
                      style={{
                        backgroundColor:
                          item.transactionType === 'EARNED'
                            ? 'rgba(74, 187, 31, 0.77)'
                            : 'rgb(238, 2, 2)',
                      }}
                    >
                      <span className='activity-item-points-value'>
                        {Math.abs(item.points).toLocaleString()}{' '}
                        {capitalizedTerminology}
                      </span>
                      <span className='activity-item-points-arrow'>
                        {item.transactionType === 'EARNED' ? '↑' : '↓'}
                      </span>
                    </div>
                  </div>
                </li>
              ))}

              {loadingMore && (
                <div className='activity-loading-more'>
                  <LottieAnimation
                    statusText='Loading more...'
                    loop={true}
                    size='small'
                    backgroundColor={themeColors?.backgroundColor}
                  />
                </div>
              )}

              {!hasMore && historyItems.length > 0 && (
                <li className='activity-end'>End of history</li>
              )}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default ActivityPanel;
